import React, { useState } from 'react';
import { 
    Container, 
    Paper, 
    Stepper, 
    Step, 
    StepLabel,
    Typography,
    Box,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    makeStyles
} from '@material-ui/core';
import { PlayCircleFilled, Close } from '@material-ui/icons';
import { useStateValue } from '../context/state/provider';
import { VIEW } from '../constants/view.constants';
import { jwtDecode } from 'jwt-decode';
import { WIZARD_STEPS, STEP_TITLES } from '../constants/wizardSteps.constants';
import NameYourAgentStep from './steps/NameYourAgentStep';
import WhiteGloveServiceStep from './steps/WhiteGloveServiceStep';
import CreationStep from './steps/CreationStep';
import ChatbotTestStep from './steps/ChatbotTestStep';
import UserDetailsStep from './steps/UserDetailsStep';

const useStyles = makeStyles((theme) => ({
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafe',
        padding: theme.spacing(2),
        transition: 'all 0.3s ease-in-out',
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1),
            alignItems: 'flex-start',
            paddingTop: theme.spacing(2)
        }
    },
    containerExpanded: {
        alignItems: 'flex-start',
        padding: theme.spacing(1)
    },
    paper: {
        width: '100%',
        maxWidth: 800,
        margin: '0 auto',
        padding: theme.spacing(4),
        borderRadius: theme.spacing(1),
        boxShadow: '0px 5px 37px 0px rgba(56, 66, 100, 0.086)',
        transition: 'all 0.3s ease-in-out',
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
            maxWidth: '100%',
            margin: 0,
            borderRadius: theme.spacing(0.5)
        },
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1.5),
            borderRadius: 0,
            boxShadow: 'none'
        }
    },
    paperChatTest: {
        maxWidth: 1040
    },
    paperExpanded: {
        maxWidth: '100%',
        height: 'calc(100vh - 16px)',
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1),
        [theme.breakpoints.down('sm')]: {
            height: 'calc(100vh - 8px)',
            padding: theme.spacing(1.5)
        }
    },
    header: {
        textAlign: 'center',
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            marginBottom: theme.spacing(2)
        }
    },
    logo: {
        fontSize: '2rem',
        fontWeight: 600,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(1),
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.75rem'
        }
    },
    subtitle: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
            marginBottom: theme.spacing(2),
            fontSize: '0.875rem'
        }
    },
    stepper: {
        marginBottom: theme.spacing(4),
        background: 'transparent',
        transition: 'all 0.3s ease-in-out',
        [theme.breakpoints.down('sm')]: {
            marginBottom: theme.spacing(2),
            '& .MuiStepLabel-label': {
                fontSize: '0.75rem'
            }
        }
    },
    stepperHidden: {
        opacity: 0,
        height: 0,
        marginBottom: 0,
        overflow: 'hidden'
    },
    headerHidden: {
        opacity: 0,
        height: 0,
        marginBottom: 0,
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out'
    },
    stepContent: {
        marginTop: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
            marginTop: theme.spacing(1)
        }
    },
    stepContentExpanded: {
        marginTop: 0
    },
    footer: {
        textAlign: 'center',
        marginTop: theme.spacing(4),
        paddingTop: theme.spacing(2),
        borderTop: '1px solid #e0e0e0',
        [theme.breakpoints.down('sm')]: {
            marginTop: theme.spacing(2),
            paddingTop: theme.spacing(1.5)
        }
    },
    footerLogo: {
        fontSize: '1.5rem',
        fontWeight: 600,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(0.5),
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.25rem'
        }
    },
    footerSubtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem'
    },
    startButton: {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        padding: theme.spacing(1.5, 3),
        fontSize: '1rem',
        fontWeight: 'bold',
        borderRadius: 8,
        textTransform: 'none',
        boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.15)',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: '0.875rem',
            padding: theme.spacing(1.25, 2.5)
        }
    },
    startButtonContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2)
        }
    },
    videoDialog: {
        '& .MuiDialog-paper': {
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: 'auto',
            margin: theme.spacing(2)
        }
    },
    videoDialogContent: {
        padding: 0,
        position: 'relative',
        backgroundColor: '#000'
    },
    closeButton: {
        position: 'absolute',
        top: theme.spacing(1),
        right: theme.spacing(1),
        color: 'white',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1,
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }
    },
    videoContainer: {
        width: '100%',
        maxWidth: '1200px',
        aspectRatio: '16 / 9',
        '& video': {
            width: '100%',
            height: '100%',
            display: 'block'
        }
    }
}));

const steps = [
    STEP_TITLES[WIZARD_STEPS.NAME_YOUR_AGENT],
    STEP_TITLES[WIZARD_STEPS.CONFIRMATION],
    STEP_TITLES[WIZARD_STEPS.CHATBOT_TEST],
    STEP_TITLES[WIZARD_STEPS.USER_DETAILS]
];

export default function WizardContainer() {
    const classes = useStyles();
    const [{ currentStep, expandedMode }, dispatch] = useStateValue();
    const [videoModalOpen, setVideoModalOpen] = useState(false);

    // Determine video URL based on environment
    const getVideoUrl = () => {
        const baseUrl = process.env.REACT_APP_CHATBOT_URL || '';
        
        if (baseUrl.includes('staging')) {
            return 'https://firebasestorage.googleapis.com/v0/b/sail-staging-4dcfb.appspot.com/o/public%2FSail%20Wizard%20Video.mp4?alt=media&token=56def759-c919-4b47-b4df-81dc81efffbb';
        } else if (baseUrl.includes('dev')) {
            return 'https://firebasestorage.googleapis.com/v0/b/sail-dev-62f69.appspot.com/o/public%2FSail%20Wizard%20Video.mp4?alt=media&token=9d392b45-ddd2-43c1-9af7-c39d5d3fe193';
        } else {
            // Production
            return 'https://firebasestorage.googleapis.com/v0/b/eco-seeker-310618.appspot.com/o/public%2FSail%20Wizard%20Video.mp4?alt=media&token=b0b47c77-095e-4c21-b244-ee97262a5164';
        }
    };

    const handleOpenVideoModal = () => {
        setVideoModalOpen(true);
    };

    const handleCloseVideoModal = () => {
        setVideoModalOpen(false);
    };

    // Token expiration check function
    const checkTokenExpiration = () => {
        const wizardToken = sessionStorage.getItem('wizardToken');
        
        if (!wizardToken) {
            // No token present, continue normally
            return true;
        }

        try {
            const decoded = jwtDecode(wizardToken);
            const now = Math.floor(Date.now() / 1000);
            
            if (decoded.exp && decoded.exp < now) {
                console.warn('🚨 JWT token has expired during wizard navigation');
                
                // Clear expired token
                sessionStorage.removeItem('wizardToken');
                
                // Redirect to error view with expiration message
                dispatch({
                    type: 'SET_ERROR',
                    error: 'Your wizard session has expired (1 hour limit). Please close this tab and generate a new wizard link from the admin panel.'
                });
                dispatch({ type: 'SET_VIEW', view: VIEW.ERROR });
                
                return false; // Token expired
            }
            
            return true; // Token valid
        } catch (error) {
            console.error('Error checking token expiration:', error);
            // If token is malformed, treat as expired
            sessionStorage.removeItem('wizardToken');
            dispatch({
                type: 'SET_ERROR',
                error: 'Invalid wizard session. Please close this tab and generate a new wizard link from the admin panel.'
            });
            dispatch({ type: 'SET_VIEW', view: VIEW.ERROR });
            return false;
        }
    };

    // Calculate active step for stepper, excluding White Glove Service step
    const getActiveStep = () => {
        if (currentStep === WIZARD_STEPS.WHITE_GLOVE_SERVICE) {
            return 0; // Show first step as active when in White Glove Service
        }
        if (currentStep <= WIZARD_STEPS.NAME_YOUR_AGENT) {
            return 0;
        }
        if (currentStep === WIZARD_STEPS.CONFIRMATION) {
            return 1;
        }
        if (currentStep === WIZARD_STEPS.CHATBOT_TEST) {
            return 2;
        }
        if (currentStep === WIZARD_STEPS.USER_DETAILS) {
            return 3;
        }
        return 0;
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case WIZARD_STEPS.NAME_YOUR_AGENT:
                return <NameYourAgentStep checkTokenExpiration={checkTokenExpiration} />;
            case WIZARD_STEPS.WHITE_GLOVE_SERVICE:
                return <WhiteGloveServiceStep checkTokenExpiration={checkTokenExpiration} />;
            case WIZARD_STEPS.CONFIRMATION:
                return <CreationStep checkTokenExpiration={checkTokenExpiration} />;
            case WIZARD_STEPS.CHATBOT_TEST:
                return <ChatbotTestStep checkTokenExpiration={checkTokenExpiration} />;
            case WIZARD_STEPS.USER_DETAILS:
                return <UserDetailsStep checkTokenExpiration={checkTokenExpiration} />;
            default:
                return <NameYourAgentStep checkTokenExpiration={checkTokenExpiration} />;
        }
    };

    const isChatTestStep = currentStep === WIZARD_STEPS.CHATBOT_TEST;

    return (
        <div className={`${classes.container} ${expandedMode ? classes.containerExpanded : ''}`}>
            <Container maxWidth={expandedMode ? false : (isChatTestStep ? "lg" : "md")}>
                <Paper className={`${classes.paper} ${isChatTestStep && !expandedMode ? classes.paperChatTest : ''} ${expandedMode ? classes.paperExpanded : ''}`}>
                    {/* Hide header for White Glove Service step and in expanded mode */}
                    {currentStep !== WIZARD_STEPS.WHITE_GLOVE_SERVICE && !expandedMode && (
                        <Box className={classes.header}>
                            <Typography variant="h5">
                                Welcome to Sail AI!
                            </Typography>
                            <Typography variant="body1" style={{ marginTop: '8px', color: '#6B747F', fontSize: '16px' }}>
                                Let's set up your first Q&A Agent. It's fast and simple.
                            </Typography>
                        </Box>
                    )}

                    {/* Hide stepper for White Glove Service step and in expanded mode */}
                    {currentStep !== WIZARD_STEPS.WHITE_GLOVE_SERVICE && !expandedMode && (
                        <Stepper 
                            activeStep={getActiveStep()} 
                            className={classes.stepper}
                            alternativeLabel
                        >
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    )}

                    {/* Start Here button - only show on initial step */}
                    {currentStep === WIZARD_STEPS.NAME_YOUR_AGENT && !expandedMode && (
                        <Box className={classes.startButtonContainer}>
                            <Button
                                className={classes.startButton}
                                onClick={handleOpenVideoModal}
                                startIcon={<PlayCircleFilled />}
                            >
                                Start Here
                            </Button>
                        </Box>
                    )}

                    <Box className={`${classes.stepContent} ${expandedMode ? classes.stepContentExpanded : ''}`}>
                        {renderStepContent()}
                    </Box>
                    
                    <Box className={classes.footer}>
                        <Typography variant="h6" className={classes.footerLogo}>
                            Sail.cx<sup style={{fontSize: '0.6em'}}>®</sup>
                        </Typography>
                        <Typography variant="body2" className={classes.footerSubtitle}>
                            By PremiseHQ
                        </Typography>
                    </Box>
                </Paper>
            </Container>

            {/* Video Modal */}
            <Dialog
                open={videoModalOpen}
                onClose={handleCloseVideoModal}
                maxWidth={false}
                className={classes.videoDialog}
            >
                <DialogContent className={classes.videoDialogContent}>
                    <IconButton
                        className={classes.closeButton}
                        onClick={handleCloseVideoModal}
                        aria-label="close"
                    >
                        <Close />
                    </IconButton>
                    <Box className={classes.videoContainer}>
                        <video controls autoPlay>
                            <source src={getVideoUrl()} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </Box>
                </DialogContent>
            </Dialog>
        </div>
    );
}
